import { useState, useEffect, useRef, useCallback } from 'react'
import { REALTIME_CONFIG, DEFAULT_CHANNELS } from './schema'
import { useRealtime } from 'aldoo-ra/Realtime/hooks'
import useInfiniteScroll from './useInfiniteScroll'

/**
 * Custom hook for managing realtime logs
 * Integrates with aldoo-logs backend via Socket.io
 */
const useRealtimeLogs = () => {
  const [logs, setLogs] = useState([])
  const [channels, setChannels] = useState(DEFAULT_CHANNELS)
  const [error, setError] = useState(null)
  const [subscriptions, setSubscriptions] = useState([])
  // Removed timeRange state - no longer filtering by date
  const [serverTime, setServerTime] = useState(null)
  const [systemStats, setSystemStats] = useState({
    totalLogs: 0,
    memoryBytes: 0,
    formattedMemory: '0 B',
    callstackCacheSize: 0
  })
  
  // Use the aldoo-ra realtime hook for socket connection
  const { socket, connected: isConnected, connecting, error: connectionError } = useRealtime('logs')
  
  const reconnectAttemptsRef = useRef(0)

  /**
   * Format bytes to human readable string
   */
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B'
    
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  /**
   * Get server time for time-based queries
   */
  const getServerTime = useCallback(async () => {
    if (!socket || !isConnected) return null
    
    return new Promise((resolve, reject) => {
      socket.emit('logs:get_server_time', {}, (response) => {
        if (response.success && response.data?.success) {
          const serverTimestamp = response.data.serverTime
          setServerTime(serverTimestamp)
          resolve(serverTimestamp)
        } else {
          // Fallback to client time if server time not available
          const clientTime = Date.now()
          setServerTime(clientTime)
          resolve(clientTime)
        }
      })
    })
  }, [socket, isConnected])

  /**
   * Fetch available channels from server
   */
  const fetchChannels = useCallback(async () => {
    if (!socket || !isConnected) return
    
    return new Promise((resolve, reject) => {
      socket.emit('logs:get_channels', {}, (response) => {
        if (response.success && response.data?.success) {
          const serverChannels = response.data.channels.map(channel => ({
            id: channel.id,
            name: channel.name || channel.id,
            enabled: channel.enabled,
            color: channel.color,
            messageCount: channel.messageCount || 0,
            messagesPerSec: channel.messagesPerSec || 0,
            messagesPerMinute: channel.messagesPerMinute || 0,
            enabledLevels: channel.enabledLevels || ['info', 'warn', 'error'],
            retention: channel.retention
          }))
          setChannels(serverChannels)
          
          // Update system stats if available
          if (response.data.systemStats) {
            setSystemStats(response.data.systemStats)
          }
          
          resolve(serverChannels)
        } else {
          const errorMsg = response.error || response.data?.error || 'Failed to fetch channels'
          setError(errorMsg)
          reject(new Error(errorMsg))
        }
      })
    })
  }, []) // Remove dependencies to prevent re-creation

  /**
   * Fetch historical logs from server based on time range
   */
  const fetchLogsRaw = useCallback(async (options = {}) => {
    if (!socket || !isConnected) {
      // Wait up to 3 seconds for reconnection
      let retries = 30
      while ((!socket || !isConnected) && retries > 0) {
        await new Promise(resolve => setTimeout(resolve, 100))
        retries--
      }
      
      if (!socket || !isConnected) {
        return { logs: [], totalCount: 0 }
      }
    }
    
    // Simplified - no time range filtering
    const { 
      channels: filterChannels = [], 
      levels = [], 
      limit = 1000
    } = options
    
    
    return new Promise((resolve, reject) => {
      const requestData = {
        channels: filterChannels,
        levels,
        limit,
        // Pass through pagination options  
        cursor: options.cursor,
        direction: options.direction,
        initialLoad: options.initialLoad
      }
      
      
      socket.emit('logs:get_logs', requestData, (response) => {
        if (response.success && response.data?.success) {
          const fetchedLogs = response.data.logs.map(log => ({
            id: log.id,
            timestamp: log.timestamp,
            sequenceId: log.sequenceId, // Include sequence ID for proper ordering
            data: log.data,
            channels: log.channels,
            level: log.level ? log.level.toLowerCase() : 'info', // Normalize to lowercase
            callstack: log.callstack,
            metadata: log.metadata,
            timeSinceLastCall: log.timeSinceLastCall, // Include time tracking data
            color: '#666' // Use default color to avoid dependency
          }))
          
          // Server already provides logs in perfect sequential order - no sorting needed
          setLogs(fetchedLogs)
          resolve({
            success: true,
            logs: fetchedLogs,
            totalCount: response.data.totalCount || fetchedLogs.length,
            hasMore: response.data.hasMore || false,
            hasBefore: response.data.hasBefore || false,
            hasAfter: response.data.hasAfter || false,
            cursors: response.data.cursors || null
          })
        } else {
          const errorMsg = response.error || response.data?.error || 'Failed to fetch logs'
          setError(errorMsg)
          reject(new Error(errorMsg))
        }
      })
    })
  }, [socket, isConnected, serverTime, getServerTime])

  // Create wrapper for infinite scroll
  const fetchLogsForInfiniteScroll = useCallback(async (options) => {
    return await fetchLogsRaw(options)
  }, [fetchLogsRaw])

  // Initialize infinite scroll hook
  const infiniteScroll = useInfiniteScroll(fetchLogsForInfiniteScroll, {
    loadThreshold: 50,
    initialPageSize: 100
  })

  // Create fetchLogs function that uses infinite scroll
  const fetchLogs = useCallback(async (options = {}) => {
    infiniteScroll.clearLogs()
    return await infiniteScroll.loadInitial(options)
  }, [infiniteScroll])

  /**
   * Subscribe to real-time logs
   */
  const subscribeToChannels = useCallback(async (channelIds = [], levels = ['INFO', 'WARN', 'ERROR']) => {
    if (!socket || !isConnected) return false
    
    return new Promise((resolve, reject) => {
      socket.emit('logs:subscribe', {
        channels: channelIds,
        levels
      }, (response) => {
        if (response.success && response.data?.success) {
          setSubscriptions(prev => [...prev, { channels: channelIds, levels }])
          resolve(true)
        } else {
          const errorMsg = response.error || response.data?.error || 'Failed to subscribe'
          setError(errorMsg)
          reject(new Error(errorMsg))
        }
      })
    })
  }, []) // Remove dependencies to prevent re-creation

  
  /**
   * Clear all logs
   */
  const clearLogs = useCallback(async (channelIds = []) => {
    if (!socket || !isConnected) {
      setLogs([])
      return true
    }
    
    return new Promise((resolve, reject) => {
      socket.emit('logs:clear_logs', { channels: channelIds }, (response) => {
        if (response.success) {
          setLogs([])
          // Channel statistics will be refreshed from server via fetchChannels
          // Don't manually reset here since server manages the actual statistics
          resolve(true)
        } else {
          const errorMsg = response.error || response.data?.error || 'Failed to clear logs'
          setError(errorMsg)
          reject(new Error(errorMsg))
        }
      })
    })
  }, []) // Remove dependencies to prevent re-creation

  /**
   * Update channel configuration
   */
  const updateChannel = useCallback(async (channelId, updates) => {
    if (!socket || !isConnected) {
      // Update locally only
      setChannels(prev => prev.map(channel =>
        channel.id === channelId ? { ...channel, ...updates } : channel
      ))
      return true
    }
    
    return new Promise((resolve, reject) => {
      socket.emit('logs:toggle_channel', {
        channelId,
        enabled: updates.enabled
      }, (response) => {
        if (response.success) {
          setChannels(prev => prev.map(channel =>
            channel.id === channelId ? { ...channel, ...updates } : channel
          ))
          resolve(true)
        } else {
          const errorMsg = response.error || response.data?.error || 'Failed to update channel'
          setError(errorMsg)
          reject(new Error(errorMsg))
        }
      })
    })
  }, []) // Remove dependencies to prevent re-creation

  // Removed changeTimeRange function - no longer filtering by date

  // Set up socket event listeners when socket becomes available
  useEffect(() => {
    if (!socket) return

    // Listen for real-time log messages - using inline handler to avoid dependency issues
    const handleLogMessage = (logEntry) => {
      // Add to infinite scroll system with enhanced log data
      const enhancedLog = {
        ...logEntry,
        level: logEntry.level ? logEntry.level.toLowerCase() : 'info', // Normalize to lowercase
        timeSinceLastCall: logEntry.timeSinceLastCall, // Include time tracking data
        color: '#666' // Default color, will be updated by UI
      }
      
      // Add to infinite scroll system
      infiniteScroll.addRealtimeLog(enhancedLog)
      
      // Update channel statistics (only increment messageCount - server will calculate proper rates)
      setChannels(prevChannels => {
        return prevChannels.map(channel => {
          if (logEntry.channels && logEntry.channels.includes(channel.id)) {
            return {
              ...channel,
              messageCount: channel.messageCount + 1
              // Note: messagesPerMinute and messagesPerSec will be updated by server via fetchChannels
            }
          }
          return channel
        })
      })
      
      // Update system stats for new log (rough estimation)
      setSystemStats(prevStats => ({
        ...prevStats,
        totalLogs: prevStats.totalLogs + 1,
        // Rough size estimation for new log entry
        memoryBytes: prevStats.memoryBytes + (JSON.stringify(enhancedLog).length * 2),
        formattedMemory: formatBytes(prevStats.memoryBytes + (JSON.stringify(enhancedLog).length * 2))
      }))
    }

    // Listen for channel rate updates
    const handleChannelRateUpdate = (data) => {
      if (data.channels && data.channels.length > 0) {
        setChannels(prevChannels => {
          return prevChannels.map(channel => {
            const updatedChannel = data.channels.find(c => c.id === channel.id)
            if (updatedChannel) {
              return {
                ...channel,
                messagesPerMinute: updatedChannel.messagesPerMinute,
                messagesPerSec: updatedChannel.messagesPerSec
              }
            }
            return channel
          })
        })
      }
    }

    socket.on('logs:message', handleLogMessage)
    socket.on('channel_rates_updated', handleChannelRateUpdate)

    return () => {
      socket.off('logs:message', handleLogMessage)
      socket.off('channel_rates_updated', handleChannelRateUpdate)
    }
  }, [socket, infiniteScroll]) // Only depend on socket and infiniteScroll

  // Fetch channels and initial logs when connection is established
  useEffect(() => {
    if (isConnected && socket) {
      // Fetch channels first
      socket.emit('logs:get_channels', {}, (response) => {
        if (response.success && response.data?.success) {
          const serverChannels = response.data.channels.map(channel => ({
            id: channel.id,
            name: channel.name || channel.id,
            enabled: channel.enabled,
            color: channel.color,
            messageCount: channel.messageCount || 0,
            messagesPerSec: channel.messagesPerSec || 0,
            messagesPerMinute: channel.messagesPerMinute || 0,
            enabledLevels: channel.enabledLevels || ['info', 'warn', 'error'],
            retention: channel.retention
          }))
          setChannels(serverChannels)
          
          // Update system stats if available
          if (response.data.systemStats) {
            setSystemStats(response.data.systemStats)
          }
          
          // Subscribe to all channels for real-time updates
          const enabledChannelIds = serverChannels.filter(c => c.enabled).map(c => c.id)
          subscribeToChannels(enabledChannelIds, ['INFO', 'WARN', 'ERROR'])
            .catch((err) => {
              console.error('Failed to subscribe to channels:', err)
            })
        } else {
          const errorMsg = response.error || response.data?.error || 'Failed to fetch channels'
          setError(errorMsg)
        }
      })
      
      // Get server time and fetch initial logs
      getServerTime()
        .then((fetchedServerTime) => {
          return fetchLogs({ serverTime: fetchedServerTime })
        })
        .catch((err) => {
          console.error('Error during initialization:', err)
        })
    }
  }, [isConnected, socket]) // Depend on both connection and socket

  // Refresh system stats periodically to keep memory count accurate
  useEffect(() => {
    if (!isConnected || !socket) return

    const interval = setInterval(() => {
      fetchChannels() // This will update both channels and system stats
    }, 5000) // Refresh every 5 seconds for more real-time messages per minute

    return () => clearInterval(interval)
  }, [isConnected, socket, fetchChannels])

  // Auto-cleanup messages per second counter - DISABLED to prevent infinite renders
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setChannels(prev => prev.map(channel => ({
  //       ...channel,
  //       messagesPerSec: Math.max(0, channel.messagesPerSec - 1)
  //     })))
  //   }, 1000) // Reset every second
  //   
  //   return () => clearInterval(interval)
  // }, [])

  // Determine connection status based on realtime hook
  const connectionStatus = connecting ? 'connecting' : isConnected ? 'connected' : 'disconnected'
  
  return {
    // Connection state
    isConnected,
    connectionStatus,
    connecting,  
    error: error || connectionError,
    
    // Data
    logs: infiniteScroll.logs,
    channels,
    subscriptions,
    serverTime,
    systemStats,
    totalCount: infiniteScroll.totalCount,
    isLoading: infiniteScroll.isLoading,
    
    // Actions
    fetchChannels,
    fetchLogs,
    clearLogs,
    updateChannel,
    subscribeToChannels,
    getServerTime,
    
    // Infinite scroll actions
    loadOlderLogs: infiniteScroll.loadOlderLogs,
    loadNewerLogs: infiniteScroll.loadNewerLogs,
    scrollToBottom: infiniteScroll.scrollToBottom,
    
    // Scroll handling
    scrollRef: infiniteScroll.scrollRef,
    handleScroll: infiniteScroll.handleScroll,
    handleWheel: infiniteScroll.handleWheel,
    handleTouchStart: infiniteScroll.handleTouchStart,
    handleTouchMove: infiniteScroll.handleTouchMove,
    shouldAutoScroll: infiniteScroll.shouldAutoScroll,
    
    // Utils
    reconnectAttempts: reconnectAttemptsRef.current,
    maxReconnectAttempts: REALTIME_CONFIG.maxReconnectAttempts,
    socket // Expose socket for advanced usage if needed
  }
}

export default useRealtimeLogs